// src/components/PullToRefresh.jsx — global pull-down-to-refresh (app feel).
// When the content under the finger is already scrolled to the top, pulling
// down reveals a circular indicator; releasing past the threshold reloads the
// route, refetching everything (cached data paints instantly thanks to the
// offline-first stores, then reconciles).
import { useState, useEffect, useRef } from 'react';

const THRESHOLD = 80;
const MAX_PULL  = 120;

// True when nothing in the touched element's scroll chain is scrolled down —
// i.e. a downward drag here is an overscroll, not a scroll.
function chainAtTop(el) {
  let node = el;
  while (node && node !== document.body) {
    if (node.scrollTop > 0) return false;
    node = node.parentElement;
  }
  return (window.scrollY || document.documentElement.scrollTop || 0) <= 0;
}

export default function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef(null);

  useEffect(() => {
    const onStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      // Ignore pulls that start on interactive surfaces prone to gestures.
      if (e.target.closest?.('input, textarea, [data-no-ptr]')) { start.current = null; return; }
      start.current = chainAtTop(e.target) ? { y: t.clientY, x: t.clientX, armed: true } : null;
    };
    const onMove = (e) => {
      const s = start.current;
      const t = e.touches?.[0];
      if (!s || !t || refreshing) return;
      const dy = t.clientY - s.y;
      const dx = Math.abs(t.clientX - s.x);
      if (dy <= 0 || dx > Math.abs(dy)) { if (dy < -8) start.current = null; setPull(0); return; }
      setPull(Math.min(MAX_PULL, dy * 0.45)); // resistance
    };
    const onEnd = () => {
      if (!start.current) return;
      start.current = null;
      // pull is the resisted (0.45×) distance, so this fires at ~80px of drag.
      setPull(p => {
        if (p >= THRESHOLD * 0.45) {
          setRefreshing(true);
          try { navigator.vibrate?.(15); } catch {}
          setTimeout(() => window.location.reload(), 250);
          return 54;
        }
        return 0;
      });
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [refreshing]);

  if (pull <= 0 && !refreshing) return null;
  const progress = Math.min(1, pull / (THRESHOLD * 0.45));

  return (
    <div style={{
      position: 'fixed', top: 8, left: '50%', zIndex: 9998,
      transform: `translateX(-50%) translateY(${Math.min(pull, 70)}px)`,
      transition: refreshing ? 'transform 0.2s' : 'none',
      pointerEvents: 'none',
    }}>
      <div style={{
        width: 38, height: 38, borderRadius: '50%', background: 'var(--white, #fff)',
        border: '1px solid var(--nat-d, #e5e7eb)', boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: refreshing ? 1 : Math.max(0.35, progress),
      }}>
        {refreshing ? (
          <span style={{ width: 17, height: 17, border: '2.5px solid var(--nat-d, #e5e7eb)', borderTopColor: 'var(--org, #FF6A00)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        ) : (
          <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke={progress >= 1 ? 'var(--org, #FF6A00)' : 'var(--txt-l, #94a3b8)'} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: `rotate(${progress * 180}deg)`, transition: 'stroke 0.1s' }}>
            <polyline points="19 12 12 19 5 12" /><line x1="12" y1="5" x2="12" y2="19" />
          </svg>
        )}
      </div>
    </div>
  );
}
